import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  IconButton, 
  Stack, 
  Box, 
  Slider,
  Popover,
  Tooltip
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTts } from '../../context/TtsContext';
import { keyframes } from '@mui/system';
import { alpha } from '@mui/material/styles';

// Animation for when audio is playing
const pulseAnimation = keyframes`
  0% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
  100% { opacity: 0.6; transform: scale(1); }
`;

const AudioControllerWidget = () => {
  const { 
    isPlaying, 
    isPreparing, 
    togglePlayPause, 
    stopTts, 
    volume, 
    setVolume,
    isAutoPlayEnabled,
    toggleAutoPlay
  } = useTts();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleVolumeClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleVolumeClose = () => {
    setAnchorEl(null);
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'volume-popover' : undefined;

  // Determine Volume Icon based on level
  const VolumeIcon = volume === 0 
    ? VolumeOffIcon 
    : volume < 0.5 
      ? VolumeDownIcon 
      : VolumeUpIcon;

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 0.5,
        pl: 1.5,
        pr: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 'auto',
        borderRadius: 2,
        border: 1,
        borderColor: isPlaying ? 'primary.main' : 'divider',
        boxShadow: 1,
        bgcolor: 'background.paper',
        transition: 'border-color 0.3s ease',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <GraphicEqIcon 
          fontSize="small" 
          color={isPlaying ? "primary" : "action"}
          sx={{ 
            animation: isPlaying ? `${pulseAnimation} 1.5s infinite ease-in-out` : 'none' 
          }}
        />
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold', 
            fontSize: '0.85rem',
            minWidth: '40px', // Prevent layout shift
            color: isPlaying ? 'primary.main' : 'text.secondary'
          }}
        >
          {isPreparing ? 'Load...' : (isPlaying ? 'Active' : 'Idle')}
        </Typography>
      </Stack>

      <Box sx={{ width: 1, height: 24, bgcolor: 'divider', mx: 0.5 }} />

      <Stack direction="row" spacing={0} alignItems="center">
        {/* Auto-Play Toggle */}
        <Tooltip title={isAutoPlayEnabled ? "Auto-Play: ON" : "Auto-Play: OFF"}>
            <IconButton
                size="small"
                onClick={toggleAutoPlay}
                color={isAutoPlayEnabled ? "primary" : "default"}
                sx={{ 
                    padding: 0.5,
                    bgcolor: isAutoPlayEnabled ? (theme) => alpha(theme.palette.primary.main, 0.1) : 'transparent'
                }}
            >
                <AutorenewIcon fontSize="small" />
            </IconButton>
        </Tooltip>

        {/* Play/Pause */}
        <IconButton 
          size="small" 
          onClick={togglePlayPause} 
          color={isPlaying ? "primary" : "default"}
          disabled={isPreparing}
          sx={{ padding: 0.5 }}
          title={isPlaying ? "Pause" : "Resume"}
        >
          {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>

        {/* Stop (Just stops, doesn't close widget) */}
        <IconButton 
          size="small" 
          onClick={stopTts} 
          disabled={!isPlaying && !isPreparing}
          sx={{ padding: 0.5, color: isPlaying ? 'error.main' : 'action.disabled' }}
          title="Stop Playback"
        >
          <StopIcon fontSize="small" />
        </IconButton>

        {/* Volume Trigger */}
        <IconButton 
          size="small" 
          onClick={handleVolumeClick}
          sx={{ padding: 0.5 }}
          title={`Volume: ${Math.round(volume * 100)}%`}
        >
          <VolumeIcon fontSize="small" color="action" />
        </IconButton>

        {/* Volume Popover */}
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleVolumeClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box sx={{ height: 100, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Slider
              orientation="vertical"
              aria-label="Volume"
              value={volume}
              min={0}
              max={1}
              step={0.01}
              onChange={handleVolumeChange}
              sx={{ 
                '& input[type="range"]': {
                  WebkitAppearance: 'slider-vertical',
                },
              }}
            />
          </Box>
        </Popover>
      </Stack>
    </Paper>
  );
};

export default AudioControllerWidget;